import { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import { Topic, User, TOPIC_CATEGORIES, TopicResolution } from '../../types'
import EmptyState from '../shared/EmptyState'
import Modal from '../shared/Modal'
import ConfirmModal from '../shared/ConfirmModal'
import ActionItemModal from './ActionItemModal'
import TopicModal from '../topics/TopicModal'
import AddTopicModal from './TopicModal'
import ViewToggle from '../topics/ViewToggle'
import type { TopicView } from '../topics/ViewToggle'
import { comparePeople } from '../../utils/peopleSort'
import TopicContextMenu from '../topics/TopicContextMenu'
import useTopicActions from '../topics/useTopicActions'

interface Props {
  topics: Topic[]
  meetingId: number
  teamId?: number
  members: User[]
  actionItemMembers?: User[]
  readOnly?: boolean
  inProgress?: boolean
  topicVoteCount?: number
  meetingTopicResolutions?: TopicResolution[]
  canFacilitate?: boolean
  currentUserId?: number
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  obstacle_to_tackle: { bg: 'rgba(237,28,36,0.15)', text: '#ED1C24' },
  info_to_share: { bg: 'rgba(100,142,138,0.15)', text: '#648E8A' },
  decision_required: { bg: 'rgba(53, 30, 29, 0.15)', text: '#1B1B1B' },
  i_need_help: { bg: 'rgba(184,122,32,0.15)', text: '#B87A20' },
  opportunity: { bg: 'rgba(47,143,94,0.15)', text: '#2F8F5E' },
}

const RESOLUTION_GROUP_LABELS: Record<string, string> = {
  tabled: 'Tabled',
  decision_made: 'Decision Made',
  done: 'Done',
  resolved: 'Resolved',
  convert_to_action: 'Converted to Action',
}

const RESOLUTION_GROUP_ORDER = ['decision_made', 'done', 'resolved', 'convert_to_action', 'tabled']

const RESOLUTION_GROUP_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  tabled: { bg: 'bg-[#351E1D]/5', text: 'text-text-primary', dot: 'bg-[#351E1D]/40' },
  decision_made: { bg: 'bg-[#15532E]/5', text: 'text-[#15532E]', dot: 'bg-[#15532E]' },
  done: { bg: 'bg-[#648E8A]/10', text: 'text-[#648E8A]', dot: 'bg-[#648E8A]' },
  resolved: { bg: 'bg-[#15532E]/5', text: 'text-[#15532E]', dot: 'bg-[#15532E]' },
  convert_to_action: { bg: 'bg-[#351E1D]/10', text: 'text-text-primary', dot: 'bg-charcoal' },
}

type SortMode = 'manual' | 'owner' | 'priority' | 'votes' | 'date_created'

// Non-manual sorts are display-only and tie-break on saved manual position,
// matching the server-side scopes used on the Team page's Topics section.
const sortTopics = (list: Topic[], mode: SortMode): Topic[] => {
  // Saved manual position, with id as a final unique tie-break so legacy
  // topics that share a position never shuffle between renders.
  const byPosition = (a: Topic, b: Topic) =>
    ((a.position ?? 0) - (b.position ?? 0)) || (a.id - b.id)
  const sorted = [...list]
  switch (mode) {
    case 'owner':
      return sorted.sort((a, b) => {
        if (!a.owner && !b.owner) return byPosition(a, b)
        if (!a.owner) return 1 // unassigned last
        if (!b.owner) return -1
        // Task #613: shared people comparator, tie-broken on manual position.
        return comparePeople(a.owner, b.owner) || byPosition(a, b)
      })
    case 'priority':
      return sorted.sort((a, b) => (Number(b.priority) - Number(a.priority)) || byPosition(a, b))
    case 'votes':
      return sorted.sort((a, b) => ((b.votes_count ?? 0) - (a.votes_count ?? 0)) || byPosition(a, b))
    case 'date_created':
      return sorted.sort((a, b) =>
        (new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) || byPosition(a, b))
    default:
      // Manual: keep the list's current order. The list state is already
      // position-sorted on load, and after an optimistic drag the array order
      // is newer than the (stale) position fields, so don't re-sort here.
      return sorted
  }
}

const getCategoryLabel = (category: string): string => {
  const cat = TOPIC_CATEGORIES.find(c => c.value === category)
  return cat?.label || category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export default function SectionTheList({
  topics, meetingId, teamId, members, actionItemMembers, readOnly, inProgress = false,
  topicVoteCount = 0, meetingTopicResolutions = [], canFacilitate = false,
  currentUserId
}: Props) {
  // The list opens in vote order (highest first). Ties fall back to saved
  // manual position, so zero-vote topics keep their appended sequence and the
  // newest topic sits last. Switching to Manual restores the drag order.
  const [sortMode, setSortModeState] = useState<SortMode>(() => {
    try { return localStorage.getItem(`meeting-topic-sort:${meetingId}`) === 'manual' ? 'manual' : 'priority' } catch { return 'priority' }
  })
  const setSortMode = (mode: SortMode) => {
    setSortModeState(mode)
    try { localStorage.setItem(`meeting-topic-sort:${meetingId}`, mode) } catch { /* storage unavailable */ }
  }
  // List/Grid view; persisted locally so switching sections isn't jarring.
  const [view, setView] = useState<TopicView>(() => {
    try {
      return localStorage.getItem('topicsView') === 'grid' ? 'grid' : 'list'
    } catch {
      return 'list'
    }
  })
  const changeView = (v: TopicView) => {
    setView(v)
    try { localStorage.setItem('topicsView', v) } catch { /* ignore */ }
  }
  // Drag reordering is only meaningful while viewing the saved manual order,
  // and stays list-only (cards in the grid aren't draggable).
  const canOrder = inProgress && canFacilitate && !readOnly
  const canReorder = canOrder && view === 'list'
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [showAddTopicModal, setShowAddTopicModal] = useState(false)
  const [resolveModal, setResolveModal] = useState<{ topic: Topic } | null>(null)
  const [resolvedNotes, setResolvedNotes] = useState('')
  const [convertModal, setConvertModal] = useState<{ topic: Topic } | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ topic: Topic } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [resetting, setResetting] = useState(false)

  const maxVotes = 3
  const canVote = topicVoteCount < maxVotes

  // Identify topics resolved/tabled in *this* meeting; those move into the
  // bottom status groups while the meeting is in progress. The active list
  // still shows pre-existing tabled topics from earlier meetings.
  const currentMeetingResolutionIds = useMemo(
    () => new Set(meetingTopicResolutions.map(r => r.topic_id)),
    [meetingTopicResolutions]
  )

  const actions = useTopicActions(topics, `/meetings/${meetingId}/topics`)
  const activeTopics = actions.rows.filter(
    t => t.status !== 'resolved' && !currentMeetingResolutionIds.has(t.id)
  )

  const sortByPosition = (list: Topic[]) =>
    [...list].sort((a, b) => ((a.position ?? 0) - (b.position ?? 0)) || (a.id - b.id))

  const initialShortTerm = sortByPosition(activeTopics.filter(t => t.topic_type !== 'long_term'))
  const initialLongTerm = sortByPosition(activeTopics.filter(t => t.topic_type === 'long_term'))

  const shortTermTopics = initialShortTerm
  const longTermTopics = initialLongTerm

  const persistOrder = (newShort: Topic[], newLong: Topic[]) => {
    // Persist via the meeting-scoped reorder endpoint, which enforces
    // in_progress + reorder permission and writes back to team topics.
    // Positions are flat across both groups, Short Term first, CAMP after.
    actions.reorder([...newShort, ...newLong])
  }

  const handleDragEnd = (result: DropResult) => {
    if (!canReorder) return
    const { source, destination } = result
    if (!destination || (destination.index === source.index && destination.droppableId === source.droppableId)) return
    const groups = { 'topics-short': sortTopics(shortTermTopics, sortMode), 'topics-long': sortTopics(longTermTopics, sortMode) }
    const from = source.droppableId as keyof typeof groups
    const to = destination.droppableId as keyof typeof groups
    const [moved] = groups[from].splice(source.index, 1)
    const type = to === 'topics-short' ? 'short_term' : 'long_term'
    groups[to].splice(destination.index, 0, { ...moved, topic_type: type })
    setSortMode('manual')
    actions.reorder([...groups['topics-short'], ...groups['topics-long']], { topic_id: moved.id, destination_type: type })
  }

  const handleVote = (topicId: number) => {
    router.post(`/meetings/${meetingId}/topics/${topicId}/vote`, {}, { preserveScroll: true })
  }

  const handleResolvedConfirm = () => {
    if (!resolveModal) return
    router.post(`/meetings/${meetingId}/topics/${resolveModal.topic.id}/resolve`, {
      resolution_type: 'resolved',
      resolution_notes: resolvedNotes,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setResolveModal(null)
        setResolvedNotes('')
        // Only a successful resolve closes the edit panel behind the dialog.
        setEditingTopic(null)
      }
    })
  }

  const handleDelete = () => {
    if (!deleteModal) return
    setDeleting(true)
    router.delete(`/meetings/${meetingId}/topics/${deleteModal.topic.id}`, {
      preserveScroll: true,
      onFinish: () => {
        setDeleting(false)
        setDeleteModal(null)
      }
    })
  }

  const handleReopen = (topicId: number) => {
    router.post(`/meetings/${meetingId}/topics/${topicId}/reopen`, {}, { preserveScroll: true })
  }

  const handleReorderByVotes = () => {
    if (!(inProgress && canFacilitate && !readOnly)) return

    // Persist a votes-desc order as the new manual order (ties broken by
    // current position asc). Topics in the bottom Tabled/Done/etc. groups are
    // not in shortTermTopics or longTermTopics, so they're untouched. Switch
    // back to Manual sort so the newly saved order is what's on screen and
    // doesn't conflict with a display-only sort.
    const newShort = sortTopics(shortTermTopics, 'votes')
    const newLong = sortTopics(longTermTopics, 'votes')

    setSortMode('manual')
    persistOrder(newShort, newLong)
  }

  const handleResetVotes = () => {
    setResetting(true)
    router.post(`/meetings/${meetingId}/topics/reset_votes`, {}, {
      preserveScroll: true,
      onFinish: () => {
        setResetting(false)
        setShowResetConfirm(false)
      }
    })
  }

  // Group bottom-section resolutions by inferred type. Tabled topics that
  // happened in this meeting are already excluded from the active list.
  const groupedResolutions = useMemo(() => {
    const groups: Record<string, TopicResolution[]> = {}
    meetingTopicResolutions.forEach(r => {
      const key = r.resolution_type || 'resolved'
      if (!groups[key]) groups[key] = []
      groups[key].push(r)
    })
    return groups
  }, [meetingTopicResolutions])

  const renderTopicRow = (
    topic: Topic,
    index: number,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => {
    const isTabled = topic.status === 'deferred'
    const colors = (topic.category && CATEGORY_COLORS[topic.category]) || { bg: '#f5f5f5', text: '#1B1B1B' }

    return (
      <div className={`border rounded-lg ${isTabled ? 'bg-[#F5F5F5] border-[#351E1D]/10' : 'bg-white border-[#351E1D]/15'}`}>
        <div className="flex items-center gap-3 p-4">
          {canReorder && (
            <div
              className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-secondary flex-shrink-0"
              {...(dragHandleProps || {})}
              aria-label="Drag to reorder"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <rect x="8" y="4" width="3" height="3" rx="0.5" />
                <rect x="13" y="4" width="3" height="3" rx="0.5" />
                <rect x="8" y="10.5" width="3" height="3" rx="0.5" />
                <rect x="13" y="10.5" width="3" height="3" rx="0.5" />
                <rect x="8" y="17" width="3" height="3" rx="0.5" />
                <rect x="13" y="17" width="3" height="3" rx="0.5" />
              </svg>
            </div>
          )}
          <button
            type="button"
            onClick={() => setEditingTopic(topic)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left"
          >
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-stone/10 text-xs font-bold text-text-primary flex-shrink-0">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-text-primary" style={{ fontSize: '16px' }}>{topic.title}</p>
                {topic.category && (
                  <span
                    className="px-2 py-0.5 rounded-full font-medium"
                    style={{ fontSize: '12px', backgroundColor: colors.bg, color: colors.text }}
                  >
                    {getCategoryLabel(topic.category)}
                  </span>
                )}
                {isTabled && (
                  <span className="px-2 py-0.5 rounded-full font-bold bg-[#351E1D]/10 text-text-primary" style={{ fontSize: '11px' }}>
                    TABLED
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {topic.owner && (
                  <span className="text-text-secondary" style={{ fontSize: '14px' }}>{topic.owner.first_name} {topic.owner.last_name}</span>
                )}
                {topic.created_at && (
                  <span className="text-text-muted" style={{ fontSize: '13px' }}>
                    Created {new Date(topic.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
          </button>

          <div className="flex items-center gap-3 flex-shrink-0">
            {teamId && !readOnly && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (canVote || topic.voted_by_current_user) handleVote(topic.id)
                }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors ${
                  topic.voted_by_current_user ? 'text-text-primary font-semibold bg-[#351E1D]/10 border border-charcoal/30' : 'text-text-secondary hover:bg-[#351E1D]/5 border border-transparent'
                } ${(canVote || topic.voted_by_current_user) ? 'cursor-pointer' : 'cursor-default opacity-50'}`}
                style={{ fontSize: '13px' }}
                title={topic.voter_names.length > 0 ? `Voted by: ${topic.voter_names.join(', ')}` : 'Vote for this topic'}
              >
                <svg className="w-3.5 h-3.5" fill={topic.voted_by_current_user ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                <span className="font-semibold">{topic.votes_count > 0 ? topic.votes_count : '0'}</span>
              </button>
            )}
            {readOnly && topic.votes_count > 0 && (
              <span className="text-text-secondary font-medium" style={{ fontSize: '14px' }}>
                {topic.votes_count} vote{topic.votes_count > 1 ? 's' : ''}
              </span>
            )}
            {topic.priority && (
              <span className="px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-[#ED1C24]/10 text-[#ED1C24] flex-shrink-0" style={{ fontSize: '11px' }}>
                PRIORITY
              </span>
            )}
            <button
              type="button"
              onClick={() => setExpandedId(expandedId === topic.id ? null : topic.id)}
              className="flex-shrink-0"
              aria-label="Toggle details"
            >
              <svg className={`w-4 h-4 text-text-secondary transition-transform ${expandedId === topic.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!readOnly && <TopicContextMenu topic={topic} teamId={teamId!}
              onEdit={() => setEditingTopic(topic)}
              onResolve={() => { setResolvedNotes(''); setResolveModal({ topic }) }}
              onConvert={() => setConvertModal({ topic })}
              onDelete={() => setDeleteModal({ topic })}
              onPriority={() => actions.priority(topic, !topic.priority)}
              onEdge={canOrder ? edge => { setSortMode('manual'); actions.edge(topic, edge) } : undefined}
            />}
          </div>
        </div>

        {expandedId === topic.id && (
          <div className="px-4 pb-4 border-t border-[#351E1D]/10">
            <div className="pt-3">
              {topic.description?.trim() && (
                <p className="text-text-primary mb-4" style={{ fontSize: '16px' }}>{topic.description}</p>
              )}

              {topic.voter_names && topic.voter_names.length > 0 && (
                <p className="text-text-secondary mb-4" style={{ fontSize: '14px' }}>
                  Voted by: {topic.voter_names.join(', ')}
                </p>
              )}

              {!readOnly && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setResolvedNotes('')
                      setResolveModal({ topic })
                    }}
                    className="px-4 py-2 rounded-lg font-semibold transition-colors bg-[#15532E] text-white hover:bg-[#15532E]/90"
                    style={{ fontSize: '14px' }}
                  >
                    Resolved
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setConvertModal({ topic })
                    }}
                    className="px-4 py-2 rounded-lg font-semibold transition-colors bg-gray-200 text-[#1B1B1B] hover:bg-gray-300"
                    style={{ fontSize: '14px' }}
                  >
                    Convert to Action Item
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteModal({ topic })
                    }}
                    className="px-4 py-2 rounded-lg font-semibold transition-colors bg-white text-[#ED1C24] border border-[#ED1C24]/30 hover:bg-[#ED1C24]/5"
                    style={{ fontSize: '14px' }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderTopicCard = (topic: Topic, index: number) => {
    const isTabled = topic.status === 'deferred'
    const colors = (topic.category && CATEGORY_COLORS[topic.category]) || { bg: '#f5f5f5', text: '#1B1B1B' }

    return (
      <div
        key={topic.id}
        className={`border rounded-lg flex flex-col h-full ${isTabled ? 'bg-[#F5F5F5] border-[#351E1D]/10' : 'bg-white border-[#351E1D]/15'}`}
      >
        <div className="p-4 flex flex-col gap-2 flex-1">
          <div className="flex items-start gap-2">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-stone/10 text-xs font-bold text-text-primary flex-shrink-0">
              {index + 1}
            </span>
            <button
              type="button"
              onClick={() => setEditingTopic(topic)}
              className="flex-1 min-w-0 text-left"
            >
              <p className="font-medium text-text-primary leading-snug" style={{ fontSize: '16px' }}>{topic.title}</p>
            </button>
            {topic.priority && (
              <span className="px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-[#ED1C24]/10 text-[#ED1C24] flex-shrink-0" style={{ fontSize: '11px' }}>
                PRIORITY
              </span>
            )}
            {!readOnly && <TopicContextMenu topic={topic} teamId={teamId!}
              onEdit={() => setEditingTopic(topic)}
              onResolve={() => { setResolvedNotes(''); setResolveModal({ topic }) }}
              onConvert={() => setConvertModal({ topic })}
              onDelete={() => setDeleteModal({ topic })}
              onPriority={() => actions.priority(topic, !topic.priority)}
              onEdge={canOrder ? edge => { setSortMode('manual'); actions.edge(topic, edge) } : undefined}
            />}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {topic.category && (
              <span
                className="px-2 py-0.5 rounded-full font-medium"
                style={{ fontSize: '12px', backgroundColor: colors.bg, color: colors.text }}
              >
                {getCategoryLabel(topic.category)}
              </span>
            )}
            {isTabled && (
              <span className="px-2 py-0.5 rounded-full font-bold bg-[#351E1D]/10 text-text-primary" style={{ fontSize: '11px' }}>
                TABLED
              </span>
            )}
          </div>

          {topic.description?.trim() && (
            <p className="text-text-secondary line-clamp-2" style={{ fontSize: '13px' }}>{topic.description}</p>
          )}

          <div className="flex items-center justify-between gap-2 mt-auto pt-2">
            <span className="text-text-secondary truncate" style={{ fontSize: '14px' }}>
              {topic.owner ? `${topic.owner.first_name} ${topic.owner.last_name}` : ''}
              {topic.created_at && (
                <span className="text-text-muted"> · Created {new Date(topic.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              )}
            </span>
            {teamId && !readOnly ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (canVote || topic.voted_by_current_user) handleVote(topic.id)
                }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors flex-shrink-0 ${
                  topic.voted_by_current_user ? 'text-text-primary font-semibold bg-[#351E1D]/10 border border-charcoal/30' : 'text-text-secondary hover:bg-[#351E1D]/5 border border-transparent'
                } ${(canVote || topic.voted_by_current_user) ? 'cursor-pointer' : 'cursor-default opacity-50'}`}
                style={{ fontSize: '13px' }}
                title={topic.voter_names.length > 0 ? `Voted by: ${topic.voter_names.join(', ')}` : 'Vote for this topic'}
              >
                <svg className="w-3.5 h-3.5" fill={topic.voted_by_current_user ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                <span className="font-semibold">{topic.votes_count > 0 ? topic.votes_count : '0'}</span>
              </button>
            ) : (
              topic.votes_count > 0 && (
                <span className="text-text-secondary font-medium flex-shrink-0" style={{ fontSize: '13px' }}>
                  {topic.votes_count} vote{topic.votes_count > 1 ? 's' : ''}
                </span>
              )
            )}
          </div>
        </div>

        {!readOnly && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-3 pt-2 border-t border-[#351E1D]/10">
            <button
              onClick={() => { setResolvedNotes(''); setResolveModal({ topic }) }}
              className="px-2.5 py-1 rounded-md font-semibold transition-colors bg-[#15532E]/10 text-[#15532E] hover:bg-[#15532E]/20"
              style={{ fontSize: '12px' }}
            >
              Resolved
            </button>
            <button
              onClick={() => setConvertModal({ topic })}
              className="px-2.5 py-1 rounded-md font-semibold transition-colors bg-gray-200 text-[#1B1B1B] hover:bg-gray-300"
              style={{ fontSize: '12px' }}
            >
              → Action
            </button>
            <button
              onClick={() => setDeleteModal({ topic })}
              className="px-2.5 py-1 rounded-md font-semibold transition-colors text-[#ED1C24] hover:bg-[#ED1C24]/5 ml-auto"
              style={{ fontSize: '12px' }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    )
  }

  const renderDraggableGroup = (label: string, labelClass: string, list: Topic[], group: 'short' | 'long', emptyText: string) => (
    <div>
      <h4 className={`text-sm font-bold tracking-wider mb-3 flex items-center gap-2 ${labelClass}`}>
        <span>{label}</span>
        <span className="text-xs font-medium bg-[#351E1D]/5 text-text-secondary px-2 py-0.5 rounded-full">{list.length}</span>
      </h4>
      {list.length > 0 || canReorder ? (
        view === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {list.map((topic, index) => renderTopicCard(topic, index))}
          </div>
        ) : !canReorder ? (
          <div className="space-y-2">
            {list.map((topic, index) => renderTopicRow(topic, index))}
          </div>
        ) : (
            <Droppable droppableId={`topics-${group}`}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2 min-h-16">
                  {!list.length && <p className="text-text-secondary italic text-sm py-3">{emptyText}</p>}
                  {list.map((topic, index) => (
                    <Draggable key={topic.id} draggableId={`topic-${topic.id}`} index={index}>
                      {(prov, snapshot) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          style={prov.draggableProps.style}
                          className={snapshot.isDragging ? 'shadow-lg rounded-lg' : ''}
                        >
                          {renderTopicRow(topic, index, prov.dragHandleProps)}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
        )
      ) : (
        <p className="text-text-secondary italic text-sm py-3">{emptyText}</p>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      {actions.error && <p role="alert" className="text-pinnacle-red">{actions.error}</p>}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase mb-2">The List of Topics</h3>
          <p className="text-base text-text-muted">Discuss and resolve open topics by priority.</p>
        </div>
        <div className="flex items-center gap-2">
          {inProgress && !readOnly && canFacilitate && (
            <>
              <button
                onClick={() => setShowAddTopicModal(true)}
                className="px-3 py-2 rounded-lg font-semibold transition-colors flex items-center gap-1.5 border bg-white text-text-primary border-[#351E1D]/20 hover:bg-[#351E1D]/5"
                style={{ fontSize: '14px' }}
                title="Add a new topic to the list"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Topic
              </button>
              <button
                onClick={handleReorderByVotes}
                className="px-3 py-2 rounded-lg font-semibold transition-colors flex items-center gap-1.5 border bg-white text-text-primary border-[#351E1D]/20 hover:bg-[#351E1D]/5"
                style={{ fontSize: '14px' }}
                title="Re-sort active topics by current vote count"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
                Reorder by Votes
              </button>
              <button
                onClick={() => setShowResetConfirm(true)}
                className="px-3 py-2 rounded-lg font-semibold transition-colors flex items-center gap-1.5 border bg-white text-text-primary border-[#351E1D]/20 hover:bg-[#351E1D]/5"
                style={{ fontSize: '14px' }}
                title="Clear everyone's votes for this meeting"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset Votes
              </button>
            </>
          )}
          {!readOnly && !inProgress && (
            <button
              onClick={() => setShowAddTopicModal(true)}
              className="bg-[#ED1C24] hover:bg-pinnacle-red-dark text-white font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
              style={{ fontSize: '16px' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Topic
            </button>
          )}
        </div>
      </div>

      {teamId && (
        <div className="flex items-center gap-3 px-3 py-2 bg-[#351E1D]/5 rounded-lg">
          <svg className="w-4 h-4 text-text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
          </svg>
          <span className="font-medium text-text-primary" style={{ fontSize: '14px' }}>Your Votes:</span>
          <div className="flex gap-1">
            {Array.from({ length: maxVotes }).map((_, i) => (
              <div key={i} className={`w-2.5 h-2.5 rounded-full ${i < topicVoteCount ? 'bg-charcoal' : 'bg-[#351E1D]/20'}`} />
            ))}
          </div>
          <span className="text-text-secondary" style={{ fontSize: '14px' }}>{topicVoteCount}/{maxVotes}</span>
          {topicVoteCount >= maxVotes && (
            <span className="text-text-secondary italic" style={{ fontSize: '13px' }}>All votes cast</span>
          )}
        </div>
      )}

      {activeTopics.length === 0 && meetingTopicResolutions.length === 0 && !canReorder ? (
        <EmptyState
          title="No Open Topics"
          description="All topics have been resolved or none have been added yet."
          action={readOnly || inProgress ? undefined : { label: 'Add Topic', onClick: () => setShowAddTopicModal(true) }}
        />
      ) : (
        <>
          {activeTopics.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <ViewToggle view={view} onChange={changeView} />
              <label htmlFor="topic-sort" className="text-text-secondary font-medium" style={{ fontSize: '14px' }}>Sort:</label>
              <select
                id="topic-sort"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="px-3 py-2 border border-stone rounded-md text-sm text-text-primary bg-white"
              >
                <option value="manual">Manual (drag)</option>
                <option value="owner">Owner</option>
                <option value="priority">Priority</option>
                <option value="votes">Vote</option>
                <option value="date_created">Date Created</option>
              </select>
            </div>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
          {renderDraggableGroup('Short Term', 'text-text-primary uppercase', sortTopics(shortTermTopics, sortMode), 'short', 'No short term topics.')}
          {renderDraggableGroup('Camp', 'text-text-primary', sortTopics(longTermTopics, sortMode), 'long', 'No Camp topics.')}
          </DragDropContext>
        </>
      )}

      {inProgress && !readOnly && meetingTopicResolutions.length > 0 && (
        <div className="space-y-3 pt-4 border-t border-[#351E1D]/10">
          <h4 className="text-sm font-bold text-text-secondary uppercase tracking-wider">Actioned This Meeting</h4>
          {RESOLUTION_GROUP_ORDER.filter(key => groupedResolutions[key]?.length).map(key => {
            const colors = RESOLUTION_GROUP_COLORS[key] || RESOLUTION_GROUP_COLORS.resolved
            const items = groupedResolutions[key]
            return (
              <details key={key} open className="group">
                <summary className={`cursor-pointer px-3 py-2 rounded-lg ${colors.bg} flex items-center gap-2 hover:opacity-90`}>
                  <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  <span className={`text-sm font-semibold ${colors.text}`}>
                    {RESOLUTION_GROUP_LABELS[key] || key}
                  </span>
                  <span className="text-xs text-text-secondary">({items.length})</span>
                  <svg className="w-3.5 h-3.5 text-text-secondary ml-auto transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="mt-2 space-y-1.5 pl-3">
                  {items.map(item => (
                    <div key={item.topic_id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-[#351E1D]/10">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{item.topic_title}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-text-secondary">
                          {item.owner_name && <span>{item.owner_name}</span>}
                          {item.topic_type === 'long_term' && (
                            <span className="font-bold text-text-primary">Camp</span>
                          )}
                          {item.resolution_notes && key === 'resolved' && (
                            <span className="italic truncate">— {item.resolution_notes}</span>
                          )}
                        </div>
                      </div>
                      {canFacilitate && (
                        <button
                          onClick={() => handleReopen(item.topic_id)}
                          className="text-xs font-semibold text-text-primary hover:underline flex-shrink-0"
                          title="Move this topic back to the active list"
                        >
                          {key === 'tabled' ? 'Un-table' : 'Reopen'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )
          })}
        </div>
      )}

      <Modal isOpen={!!resolveModal} onClose={() => { setResolveModal(null); setResolvedNotes('') }} title="Resolve Topic">
        <div className="space-y-4">
          <div>
            <label className="block font-medium text-text-primary mb-1" style={{ fontSize: '14px' }}>Notes <span className="text-text-secondary">(optional)</span></label>
            <textarea
              value={resolvedNotes}
              onChange={(e) => setResolvedNotes(e.target.value)}
              placeholder="Add any notes about how this was resolved"
              className="w-full rounded-lg border border-[#351E1D]/20 px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24] min-h-[80px] resize-y"
              style={{ fontSize: '16px' }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setResolveModal(null); setResolvedNotes('') }} className="px-4 py-2 font-medium text-text-primary hover:bg-[#351E1D]/5 rounded-lg transition-colors" style={{ fontSize: '16px' }}>Cancel</button>
            <button
              onClick={handleResolvedConfirm}
              className="bg-[#15532E] hover:bg-[#15532E]/90 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
              style={{ fontSize: '16px' }}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteModal}
        title="Delete this topic?"
        message="This permanently removes the topic. It will no longer appear in the active list, the meeting recap, or the summary email. This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        submitting={deleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteModal(null)}
      />

      {convertModal && (
        <ActionItemModal
          isOpen={!!convertModal}
          onClose={() => setConvertModal(null)}
          meetingId={meetingId}
          teamId={teamId}
          teamMembers={actionItemMembers ?? members}
          convertTopicId={convertModal.topic.id}
          defaultTitle={convertModal.topic.title}
          defaultDescription={convertModal.topic.description || ''}
          onConverted={() => setEditingTopic(null)}
        />
      )}

      {editingTopic && teamId && (
        // Keyed on the topic id so picking a different topic while the panel
        // is open re-initializes the form with that topic's values, while the
        // ~10s meeting refresh (same id) never resets a mid-edit form.
        <TopicModal
          key={editingTopic.id}
          teamId={teamId}
          meetingId={meetingId}
          teamMembers={members}
          topic={editingTopic}
          canDelete={!readOnly}
          onClose={() => setEditingTopic(null)}
          // Solved / Convert open a modal ABOVE the panel; the panel stays
          // mounted so cancelling either one leaves the edit draft intact. It
          // closes only once the resolve/convert actually succeeds.
          onResolve={!readOnly ? () => {
            setResolvedNotes('')
            setResolveModal({ topic: editingTopic })
          } : undefined}
          onConvert={!readOnly ? (draft) => {
            // Merge the in-progress edit-form values over the saved topic so
            // unsaved title/description edits survive into the conversion modal.
            setConvertModal({ topic: { ...editingTopic, ...draft } })
          } : undefined}
        />
      )}

      <ConfirmModal
        isOpen={showResetConfirm}
        title="Reset everyone's votes?"
        message="This clears every attendee's votes for this meeting. Each person's vote counter goes back to zero. This cannot be undone."
        confirmLabel="Reset Votes"
        cancelLabel="Cancel"
        variant="warning"
        submitting={resetting}
        onConfirm={handleResetVotes}
        onClose={() => setShowResetConfirm(false)}
      />

      <AddTopicModal
        isOpen={showAddTopicModal}
        onClose={() => setShowAddTopicModal(false)}
        meetingId={meetingId}
        teamMembers={members}
        currentUserId={currentUserId}
      />
    </div>
  )
}
